"use client";

/**
 * InstallSuccessNewsletter: the newsletter offer at the "It worked" moment
 * (audit X9 / LOOP-3).
 *
 * It mounts only after the visitor has said the install worked, so it can never
 * sit between them and the download or the install steps. Inline, hairline
 * separated, skippable by ignoring it: no modal, no toast, no card.
 *
 * Signs up through the existing /api/newsletter route, which owns validation,
 * the durable rate limit and the welcome email.
 */

import { useCallback, useEffect, useId, useRef, useState } from "react";

import { withRequestProofHeaders } from "@/lib/request-proof";
import { trackNewsletterOfferShown, trackNewsletterSignup } from "@/lib/tracking";
import { announce } from "@/lib/announce";
import { useSessionEmail } from "@/hooks/useSessionEmail";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const EMAIL_FORMAT_MESSAGE = "Enter an address in the form name@example.com";
const SEND_FAILED_MESSAGE = "Couldn't save that right now. Try again in a moment.";

export type InstallSuccessNewsletterSurface = "receipt" | "return_visit";

/** Newsletter `source` values; both are on the /api/newsletter allow-list. */
const SOURCE: Record<InstallSuccessNewsletterSurface, string> = {
  receipt: "install_worked",
  return_visit: "install_checkin",
};

type Status = "idle" | "sending" | "done";

export function InstallSuccessNewsletter({
  surface,
  className,
}: {
  surface: InstallSuccessNewsletterSurface;
  className?: string;
}) {
  const { email, setEmail, isPrefilled, changeEmail, rememberEmail } = useSessionEmail();
  const [status, setStatus] = useState<Status>("idle");
  const [error, setError] = useState<string | null>(null);
  const [doneMessage, setDoneMessage] = useState("");
  const inputId = useId();
  const errorId = `${inputId}-error`;
  const inputRef = useRef<HTMLInputElement | null>(null);

  const shown = useRef(false);
  useEffect(() => {
    if (shown.current) return;
    shown.current = true;
    trackNewsletterOfferShown({ surface });
  }, [surface]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!EMAIL_REGEX.test(email.trim())) {
        setError(EMAIL_FORMAT_MESSAGE);
        inputRef.current?.focus();
        return;
      }
      setError(null);
      setStatus("sending");
      try {
        const res = await fetch("/api/newsletter", {
          method: "POST",
          headers: await withRequestProofHeaders({ "Content-Type": "application/json" }),
          body: JSON.stringify({ email: email.trim(), source: SOURCE[surface] }),
        });
        const data = (await res.json().catch(() => ({}))) as {
          message?: string;
          error?: string;
          alreadySubscribed?: boolean;
        };
        if (res.ok || data.alreadySubscribed) {
          rememberEmail(email);
          const message = data.message ?? "You're in.";
          setDoneMessage(message);
          setStatus("done");
          announce(message, "polite");
          trackNewsletterSignup({ surface });
          return;
        }
        setError(data.error ?? SEND_FAILED_MESSAGE);
      } catch {
        setError(SEND_FAILED_MESSAGE);
      }
      setStatus("idle");
    },
    [email, rememberEmail, surface]
  );

  return (
    <div
      className={`border-t border-[color:var(--tls-border-subtle)] pt-3 ${className ?? ""}`}
    >
      {status === "done" ? (
        <p className="m-0 text-sm font-medium text-[color:var(--tls-fg-strong)]">{doneMessage}</p>
      ) : (
        <form onSubmit={handleSubmit} noValidate aria-busy={status === "sending" || undefined}>
          <label
            htmlFor={inputId}
            className="block text-sm font-medium text-[color:var(--tls-fg-strong)]"
          >
            Want the next good one?
          </label>
          <p className="mb-2 mt-0.5 text-xs text-[color:var(--tls-fg-subtle)]">
            Chime of the Week: one short email when fresh sounds land. Unsubscribe from any email.
          </p>
          <div className="flex flex-wrap gap-2">
            <input
              ref={inputRef}
              id={inputId}
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (error && EMAIL_REGEX.test(e.target.value.trim())) setError(null);
              }}
              placeholder="name@example.com"
              autoComplete="email"
              maxLength={254}
              aria-invalid={error ? true : undefined}
              aria-describedby={error ? errorId : undefined}
              disabled={status === "sending"}
              className="tlsr-focus min-w-0 flex-[1_1_12rem] rounded-none border px-3 text-sm"
              style={{
                minHeight: "var(--tls-field-h)",
                background: "var(--tls-surface)",
                borderColor: error ? "var(--tls-fg-strong)" : "var(--tls-border)",
                color: "var(--tls-fg-strong)",
              }}
            />
            <button
              type="submit"
              disabled={status === "sending"}
              className="tlsr-focus rounded-[var(--tls-radius-pill)] px-5 text-sm font-semibold disabled:opacity-60"
              style={{
                minHeight: 44,
                background: "var(--tls-fg-strong)",
                color: "var(--tls-canvas)",
                cursor: status === "sending" ? "wait" : "pointer",
              }}
            >
              {status === "sending" ? "Saving…" : "Send me new sounds"}
            </button>
          </div>
          {isPrefilled && (
            <p className="mt-1.5 text-xs text-[color:var(--tls-fg-subtle)]">
              Using the address you entered earlier.{" "}
              <button
                type="button"
                onClick={changeEmail}
                className="tlsr-focus underline underline-offset-2"
                style={{ color: "var(--tls-fg-muted)" }}
              >
                change it
              </button>
            </p>
          )}
          {error && (
            <p
              id={errorId}
              role="alert"
              className="mt-1.5 text-xs font-bold text-[color:var(--tls-fg-strong)]"
            >
              {error}
            </p>
          )}
        </form>
      )}
    </div>
  );
}
